
import Vue from 'vue'
import { TweenLite } from 'gsap'
import $ from 'domtastic'
import { CustomEase } from '../../utils/CustomEase.js'
import { isMobile } from 'mobile-device-detect'
import { CSS2RGB } from '@/common/utils/colorUtils'
import { ProjectState } from '../../channel.types'
import { getStore } from '../../store/store'
import injectCSS from '@/common/utils/injectCSS'
import { VPopover } from 'v-tooltip'

import doneCheck from '@/apps/channel/assets/done_outline-24px.svg'

require('@/common/assets/alex-core-layout-flexbox.css')

/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//
//  Example:
//
//  <hotspot id="paragraph_hotspot" type="comment" trigger="hover">
//
//  id. string. required. the html id property for the outermost element of the hotspot.
//
//  submitted_message. string. optional. Default is "Comment Sent!".
//     This is the little message that pops up when the user has submitted a comment without errors.
//
//  title. string. optional. default is "". This is the title of the comment box.
//
//  class. string. optional.
//     You can pass in an extra css class that will be appended to the outer-container
//     of the ui element (as opposed to the highlight element which of course doesn't exist for these "block" components)
//
//  Custom CSS: Use the channel-wide CSS overrides in the lumiere-admin. Use the id of a particular element as the CSS selector.
//
//    #paragraph_hotspot {
//      background:#000066;
//      color:white;
//      border-radius:5px;
//    }
//
//  You can make this floatable with the following custom class in your css.
//
//    #paragraph_hotspot {
//      position: relative;
//      float: left;
//      width: 50%;
//      margin-right:10px;
//    }
//
//
//  You can also pass in the extra_ui_class, and then coordinate that with the channel-wide-css to alter the styles of multiple
//  CommentBlock instances at once.
//
//  More examples: To change the background of the comment block and then change the button color,
//  use these classes along with the extra_ui_class parameter "comment-blocks-b" for as many instances of CommentBlock as you like.
//
//  .comment-blocks-b {
//    background:#0066CC;
// `}
//  .comment-blocks-b .md-button {
//    background:#FF00FF;
//  }
//
//  Additional css-classes that you can target this way:
//
//     .hs-comment-block__chars-remaining-warn
//     .hs-comment-block__chars-remaining
//     .hs-comment-block--font
//     .hs-comment-block__success-message
//     .hs-comment-block__text-area-mobile
//     .hs-comment-block__text-area
//     ,hs-comment-block__ui
//
//   You can also target <textarea> and <button> element types.
//
//
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export default Vue.extend({
  props: {
    id: String,
    title: String,
    submittedMessage: {
      default: 'Comment sent!',
      type: String,
    },
  },
  data: () => {
    return {
      msg: '',
      maxLength: 1000,
      store: getStore(),
      submittedWithError: false,
      showSentMessage: false,
      showPopover: true,
      errors: [],
      customEase: CustomEase.create(
        'custom',
        'M0,0 C0.126,0.382 0.288,1.632 0.446,1.78 0.638,1.96 0.818,1 1,1'
      ),
      afterSentTimer: null,
    }
  },

  components: {
    VPopover,
  },

  computed: {
    hasTitle() {
      return this.title && this.title != ''
    },
    charsRemainingClasses() {
      const hasWarnColor = this.ratioCharRemaining < 0.1
      return [
        'al-box',
        'hs-comment-block__gutter-element-margin-right',
        {
          'hs-comment-block__chars-remaining-warn': hasWarnColor,
          'hs-comment-block__chars-remaining': !hasWarnColor,
        },
      ]
    },
    errorString() {
      return this.errors.length > 0 ? this.errors.join('<br/>') : ''
    },
    doneCheckUrl() {
      return doneCheck
    },
    charsRemaining() {
      return this.maxLength - this.msg.length
    },
    ratioCharRemaining() {
      return this.charsRemaining / this.maxLength
    },
    touchBasedUI() {
      return isMobile
    },
    loading() {
      return this.store.state.channel.article_engagements.article_loading
    },
    primaryColor() {
      return this.store.state.channel.project.primary_color
    },
    defaultBackgroundColor() {
      let color = this.primaryColor || this.backgroundColorDefault
      let alpha = this.backgroundAlphaDefault
      let rgb: any = CSS2RGB(color)
      let backgroundColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`
      return backgroundColor
    },

    defaultTextColor() {
      return this.store.state.channel.project.contrast_color
    },
  },

  methods: {
    recordCommentEngagement() {
      this.errors = []

      if (this.msg.length > this.maxLength) {
        this.submittedWithError = true
        this.errors.push(
          `Your comment must be ${this.maxLength} characters or less!`
        )
      }

      if (this.msg.length == 0) {
        this.submittedWithError = true
        this.errors.push(`Your comment cannot be empty!`)
      }

      if (this.errors.length == 0) {
        this.store.dispatch('article_interaction', {
          hotspotId: this.id,
          hotspotValue: this.msg,
        })

        this.submittedSuccess()
      }
    },

    submittedSuccess() {
      this.showSentMessage = true

      TweenLite.to(this.$refs.sentMessage, 0.2, {
        top: '0px',
        ease: this.customEase,
      })

      this.afterSentTimer = setTimeout(() => {
        this.resetForCloseState()
      }, 1500)
    },

    resetForCloseState() {
      this.msg = ''
      this.$refs.sentMessage.style.top = '30px'
      this.showSentMessage = false
      this.submittedWithError = false

      if (this.afterSentTimer) {
        clearTimeout(this.afterSentTimer)
        this.afterSentTimer = null
      }
    },
  },

  watch: {
    msg(newVal, oldVal) {
      this.submittedWithError = false
    },
  },
})
